import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  HttpResponsesHandlersFactory,
  IHttpResponsesHandler,
} from '../handlers/http-responses-handler';
import { ImpersonationInfo, IUser } from '../interfaces/user';
import { IUserFilter } from '../interfaces/user-filter';
import { EditService } from './edit.service';
import { LoggerService } from './logger.service';
import { TaggedLogger } from './taggedLogger';
import { IPageResult } from '../interfaces/page-result';
import { IUserManagementFilter } from '../interfaces/user-management-filter';
import { IUserReport } from '../interfaces/user-report.interface';
import FileSaver from 'file-saver';
import { MessageService } from 'primeng';
import { Message } from '../classes/Message';
import { Subject } from 'rxjs';
import moment from 'moment';
import { IUserEmergencyContact } from '../interfaces/emergency-contact/user-emergency-contact.dto.interface';
import { IRelationshipType } from '../interfaces/emergency-contact/relationship-type.dto.interface';
import { ITaskStep } from '../interfaces/task-info';
import localforage from 'localforage';
import { ReceiveSummaryEnum } from '../enums/receive-summary.enum';
import { LOCAL_STORAGE_CALL_STATE, LOCAL_STORAGE_RECENT_CALLS } from './telephony.service';

const LOCAL_STORAGE_CURRENT_USER = 'main.current_user';

@Injectable({ providedIn: 'root' })
export class UserService extends EditService<IUser, IUserFilter> {
  private logger: TaggedLogger;

  user$ = new Subject<IUser>();

  logout$ = new Subject();

  url = '/api/users';
  loginUrl = '/account/login';
  entityTitle = 'User';

  get currentUser(): IUser {
    try {
      let json = localStorage.getItem(LOCAL_STORAGE_CURRENT_USER);
      return JSON.parse(json);
    } catch (error) {
      this.logger.error('Error parsing json', error);
      return null;
    }
  }

  set currentUser(value: IUser) {
    if (value) {
      localStorage.setItem(LOCAL_STORAGE_CURRENT_USER, JSON.stringify(value));
    } else {
      localStorage.removeItem(LOCAL_STORAGE_CURRENT_USER);
    }
    this.user$.next(value);
  }

  constructor(
    http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    loggerService: LoggerService,
    private messageService: MessageService,
  ) {
    super(http, handlersFactory);

    this.logger = loggerService.taggedLogger('UserService');
  }

  public resendInvitation(user: any): Promise<any> {
    return this.handleRequest(
      this.http.post<any>('/api/users/resendinvitation', user),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Invitation sent.',
    );
  }

  inviteUser(user: IUser): Promise<any> {
    return this.handleRequest(
      this.http.post<any>('/api/users/inviteuser', user),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Invitation sent.',
    );
  }

  saveProfile(user: IUser) {
    return this.handleRequest(
      this.http.post<any>('/api/users/saveprofile', user),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Information updated.',
    );
  }

  save(user: IUser) {
    return this.handleRequest(
      this.http.post<any>('/api/users/save', user),
      this.handlersFactory.getForUpdate(this.entityTitle),
    );
  }

  updateReceiveSummary(id: string, receiveSummary: ReceiveSummaryEnum) {
    return this.handleRequest(
      this.http.put<ReceiveSummaryEnum>(`/api/users/${id}/receiveSummary`, receiveSummary),
      this.handlersFactory.getForUpdate(this.entityTitle),
    );
  }

  getCurrentUserOrg() {
    return this.handleRequest(
      this.http.get<any>('api/users/getcurrentuserorg'),
      this.handlersFactory.getForReadAll(),
    );
  }

  getOriginalUserOrg() {
    return this.handleRequest(
      this.http.get<any>('api/users/getoriginaluserorg'),
      this.handlersFactory.getForReadAll(),
    );
  }

  isUserInRole(roleAbbr: string) {
    return this.handleRequest(
      this.http.get<any>(`api/users/isUserInRole/${roleAbbr}`),
      this.handlersFactory.getForReadAll(),
    );
  }

  getLoggedUser() {
    return this.handleRequest(
      this.http.get<IUser>('api/users/getloggeduser'),
      this.handlersFactory.getForReadAll(),
    );
  }

  getMobileUsers() {
    return this.handleRequest(
      this.http.get<any>('api/users/mobile'),
      this.handlersFactory.getForReadAll(),
    );
  }

  //saveProfile(formValues) {
  //    return this.handleRequest(
  //        this.http.post<any>('api/users/saveProfile', formValues),
  //        this.handlersFactory.getForUpdate(this.entityTitle)
  //    );
  //}

  async impersonateUser(userId) {
    await this.tidyLocalStorageWhenSwitchingUsers();
    return this.handleRequest(
      this.http.post('api/users/impersonate', userId),
      this.handlersFactory.getDefault(),
    );
  }

  async clearLocalStorage() {
    // dont clear all local forage because it contains the job screen view type and clicked routes.
    await localforage.removeItem('ws-token');
    await localforage.removeItem(LOCAL_STORAGE_CALL_STATE);
    await localforage.removeItem(LOCAL_STORAGE_RECENT_CALLS);
    localStorage.clear();
  }

  async tidyLocalStorageWhenSwitchingUsers() {
    await localforage.removeItem('ws-token');
  }

  async stopImpersonateUser() {
    await this.tidyLocalStorageWhenSwitchingUsers();
    return this.handleRequest(
      this.http.post('api/users/stopimpersonate', null),
      this.handlersFactory.getDefault(),
    );
  }

  isUserImpersonating(): Promise<ImpersonationInfo> {
    return this.handleRequest(
      this.http.get<any>('api/users/isuserimpersonating'),
      this.handlersFactory.getDefault(),
    );
  }

  canImpersonate(id: string, handler?: IHttpResponsesHandler): Promise<boolean> {
    return this.handleRequest(
      this.http.get<boolean>('api/users/canimpersonate?id=' + id),
      handler !== undefined ? handler : this.handlersFactory.getDefault(),
    );
  }

  async logout(reload = true) {
    try {
      await this.handleRequest(
        this.http.post<any>('/api/account/logout', ''),
        this.handlersFactory.getDefault(),
      );

      this.logout$.next();

      this.currentUser = null;
      await this.clearLocalStorage();
    } catch (error) {
      this.logger.error('Error logging out', error);
    }

    if (reload) {
      this.redirectToLogin();
    }
  }

  redirectToLogin() {
    this.logger.debug('Redirecting to login with Logged Out message');
    window.location.href = `${this.loginUrl}?logout=true`;
  }

  lockUplock(user) {
    return this.handleRequest(
      this.http.post(`${this.url}` + '/lockunlock', user),
      this.handlersFactory.getForUpdate(this.entityTitle),
    );
  }

  getUserManagementReport(filterCommand: IUserManagementFilter): Promise<IPageResult<IUserReport>> {
    return this.handleRequest<IPageResult<IUserReport>>(
      this.http.get<IPageResult<IUserReport>>(`${this.url}/userManagementReport`, {
        params: this.constructHttpParams(filterCommand),
      }),
      this.handlersFactory.getForReadByFilter(),
    );
  }

  public async downloadUserManagementReport(filter: IUserManagementFilter) {
    const filename = `UserManagementReport_${moment().format('YYYYMMMMDo_h:mm')}.csv`;

    const params = this.constructHttpParams(filter);
    const report = this.http.get(`${this.url}/downloadToCSV`, {
      params: params,
      responseType: 'blob',
    });

    report.subscribe(
      (x) => {
        this.logger.debug('Got data to export as CSV.');
        this.handleDownloadFile(x, filename);
      },
      (error) => {
        this.logger.error('Could not download CSV', error);
        this.messageService.add(Message.Error('Could not download CSV'));
      },
      () => {
        this.messageService.add(Message.Success('Downloaded successful'));
      },
    );

    return report;
  }

  getUsersWithRoles(filter: IUserFilter): Promise<IPageResult<IUser>> {
    return this.handleRequest(
      this.http.get<IPageResult<IUser>>(`${this.url}` + '/usersWithRoles', {
        params: this.constructHttpParams(filter),
      }),
      this.handlersFactory.getForReadByFilter(),
    );
  }

  getUsersFromCustomer(customerId: number): Promise<IUser[]> {
    return this.handleRequest(
      this.http.get<IUser[]>(`${this.url}` + `/${customerId}/getUsers`),
      this.handlersFactory.getForReadAll(),
    );
  }

  reset2FA(user) {
    return this.handleRequest(
      this.http.post(`${this.url}` + '/reset2FA', user),
      this.handlersFactory.getForUpdate(this.entityTitle),
    );
  }

  private handleDownloadFile(blob, filename) {
    FileSaver.saveAs(blob, filename);
  }

  deleteProfileImage(userId: string) {
    return this.http
      .delete<IPageResult<IUser>>(`${this.url}` + '/' + userId + '/profileImage')
      .toPromise();
  }

  getCustomerProfileUserOrgStructure(userId: string): Promise<ICustomerProfileUserOrgStructure> {
    return this.http
      .get<ICustomerProfileUserOrgStructure>(`/api/users/${userId}/org-structure`)
      .toPromise();
  }

  getRelationshipTypes(): Promise<IRelationshipType[]> {
    return this.http.get<IRelationshipType[]>(`/api/users/relationship-types`).toPromise();
  }

  getEmergencyContact(userId: string): Promise<IUserEmergencyContact> {
    return this.http
      .get<IUserEmergencyContact>(`/api/users/${userId}/emergency-contact`)
      .toPromise();
  }

  saveEmergencyContact(contact: IUserEmergencyContact) {
    return this.http.post('/api/users/emergency-contact', contact).toPromise();
  }

  patchEmergencyContact(id: number, contactPatch: any) {
    return this.http
      .patch<ITaskStep>(`/api/users/emergency-contact/${id}`, contactPatch)
      .toPromise();
  }

  getUserData(userId: string): Promise<IUser> {
    return this.http.get<IUser>(`/api/users/${userId}/basic-data`).toPromise();
  }
}
